import { Div, IRouteEvent, router, emitter } from '@codesuma/baseline'
import { BackIcon } from 'components/back-icon'
import { ListItem } from '../../components/list-item'
import { ThemeToggle } from '../../components/theme-toggle'
import publicListsService, { PublicList } from '../../services/public-lists'
import helpers from '../../helpers'
import style from './index.module.css'

// Read-only view of one public list (/l/:slug). It looks like one of your own
// lists — the circle's color, the same rows — so a public circle opens into it
// with the same morph. Nothing here can be changed: the rows are read-only, and
// the add bar's slot holds the one action, "Save a copy".
export const PublicListPage = () => {
    const base = Div()
    base.addClass(style.base)

    let slug = ''
    let viewing = false

    const header = Div()
    header.addClass(style.header)
    const back = BackIcon('currentColor')
    back.addClass(style.back)
    const title = Div()
    title.addClass(style.title)
    header.append(back, title)

    const desc = Div()
    desc.addClass(style.desc)

    // The subline carries the promise the label alone can't: it's a private
    // copy, not a shared list.
    const saveBar = Div()
    saveBar.addClass(style.saveBar)
    const saveBtn = Div('Save a copy')
    saveBtn.addClass(style.saveBtn)
    const saveNote = Div('You get your own private copy — only you can see it.')
    saveNote.addClass(style.saveNote)
    saveBar.append(saveBtn, saveNote)

    // The page wears the circle's colors; the button is drawn inverted so it
    // stands out on that surface in either theme.
    const paint = () => {
        base.style({ backgroundColor: helpers.surfaceForId(slug), color: helpers.textForId(slug) })
        saveBtn.style({ background: helpers.textForId(slug), color: helpers.surfaceForId(slug) })
    }
    emitter.on('theme', () => { if (viewing) paint() })

    const listEl = Div()
    listEl.addClass(style.list)

    base.append(header, desc, saveBar, listEl, ThemeToggle())

    let current: PublicList | null = null
    let saving = false

    // The rows look tickable, so people will tap them — turn that into a nudge
    // toward the real action instead of a dead tap.
    const pulse = () => {
        saveBtn.removeClass(style.pulse)
        void saveBtn.el.offsetWidth // restart the animation from the top
        saveBtn.addClass(style.pulse)
    }
    listEl.el.onclick = () => { if (current && !saving) pulse() }
    saveBtn.el.addEventListener('animationend', () => saveBtn.removeClass(style.pulse))

    // Slide pieces up into place one after another, as the list page does.
    const rise = (els: ReturnType<typeof Div>[], start: number) => els.forEach((el, i) => {
        el.style({ opacity: '0', transform: 'translateY(14px)', transition: 'none' })
        el.style({ opacity: '', transform: '', transition: 'opacity 0.3s ease, transform 0.3s ease' }, start + Math.min(i, 12) * 45)
    })

    // Built fresh each time — baseline's empty() strips children permanently.
    const renderStatus = (msg: string, retry?: () => void) => {
        listEl.empty()
        const s = Div(msg)
        s.addClass(style.status)
        listEl.append(s)
        if (retry) {
            const r = Div('Try again')
            r.addClass(style.retry)
            r.el.onclick = retry
            listEl.append(r)
        }
    }

    const render = (pub: PublicList) => {
        title.text(pub.title)
        desc.text(pub.description)
        listEl.empty()
        const rows = pub.items.map((it, i) => ListItem({
            id: `${pub.slug}-${i}`, list_id: '', name: it.name, checked: false, category: 'General',
            kind: it.kind === 'heading' ? 'heading' : 'item',
            user_id: '', synced: true, created_at: '', updated_at: '',
        }, true))
        listEl.append(...rows)
        rise(rows, 0)
    }

    const save = async () => {
        if (!current || saving) return
        saving = true
        saveBtn.text('Saving…')
        try {
            const id = await publicListsService.saveCopy(current)
            saveBtn.text('Saved ✓')
            const name = current.title
            // Land straight in the new copy — same color, so the list just
            // seems to become editable.
            setTimeout(() => router.goto('/list/' + id, { name }), 300)
        } catch (err) {
            console.error('Failed to save a copy:', err)
            saveBtn.text('Save a copy')
            saving = false
        }
    }
    saveBtn.el.onclick = save

    const load = async (want: string) => {
        renderStatus('Loading…')
        try {
            const pub = await publicListsService.getBySlug(want)
            if (slug !== want) return // navigated away while loading
            if (!pub) return renderStatus('This list is no longer available.')
            current = pub
            render(pub)
            saveBar.style({ display: 'block' })
        } catch (err) {
            console.warn('Failed to load public list:', err)
            if (slug !== want) return
            renderStatus("Couldn't load this list — are you offline?", () => load(want))
        }
    }

    base.on('enter', ({ params, from, data }: IRouteEvent) => {
        slug = params.slug
        viewing = true
        // Cold open (a shared /l/ link): no history to go back to — lead to
        // Discover instead of stranding the visitor on a dead back button.
        back.el.onclick = from ? () => router.back() : () => router.goto('/discover')
        paint()
        // Reveal a beat later so the tapped circle is seen growing in first.
        base.style({ opacity: '1', pointerEvents: 'auto' }, 200)
        // Reset state — the page instance is cached across visits. The circle
        // passed its title along, so the header is right before the fetch.
        current = null
        saving = false
        title.text(data?.name || '')
        desc.text('')
        saveBtn.text('Save a copy')
        saveBar.style({ display: 'none' })
        rise([header, desc, saveBar], 230)
        load(slug)
    })

    base.on('exit', () => {
        viewing = false
        base.style({ opacity: '0', pointerEvents: 'none' })
    })

    return base
}
